// Gamma 分布の解析関数（決定的, 乱数なし）。事後 Gamma の 90% 信用区間（5%/95% 分位点, §5.2）に使う。
// 参考: Numerical Recipes の gammp（級数 + 連分数）と Lanczos 近似。

const LANCZOS_G = 7;
const LANCZOS_C = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
  -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
  1.5056327351493116e-7,
];

/** ln Γ(x)（Lanczos 近似, x>0）。 */
export function lgamma(x: number): number {
  if (x < 0.5) {
    // 反射公式: Γ(x)Γ(1-x) = π/sin(πx)。
    return Math.log(Math.PI / Math.sin(Math.PI * x)) - lgamma(1 - x);
  }
  const xm = x - 1;
  let a = LANCZOS_C[0]!;
  const tt = xm + LANCZOS_G + 0.5;
  for (let i = 1; i < LANCZOS_G + 2; i++) {
    a += LANCZOS_C[i]! / (xm + i);
  }
  return 0.5 * Math.log(2 * Math.PI) + (xm + 0.5) * Math.log(tt) - tt + Math.log(a);
}

/** 正則化下側不完全ガンマ P(a,x) = γ(a,x)/Γ(a)（標準 Gamma(shape=a, rate=1) の CDF）。 */
export function regLowerGamma(a: number, x: number): number {
  if (x <= 0 || a <= 0) return 0;
  if (x < a + 1) {
    // 級数展開。
    let ap = a;
    let sum = 1 / a;
    let del = sum;
    for (let n = 0; n < 300; n++) {
      ap += 1;
      del *= x / ap;
      sum += del;
      if (Math.abs(del) < Math.abs(sum) * 1e-15) break;
    }
    return sum * Math.exp(-x + a * Math.log(x) - lgamma(a));
  }
  // 連分数で Q=1-P を求める。
  const FPMIN = 1e-300;
  let b = x + 1 - a;
  let c = 1 / FPMIN;
  let d = 1 / b;
  let h = d;
  for (let i = 1; i < 300; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = b + an / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < 1e-15) break;
  }
  const q = Math.exp(-x + a * Math.log(x) - lgamma(a)) * h;
  return 1 - q;
}

/** P(a,y)=q を満たす標準 Gamma(shape=a, rate=1) の分位点 y（二分法）。 */
function invRegLowerGamma(a: number, q: number): number {
  if (q <= 0) return 0;
  if (q >= 1) return Infinity;
  // 上限をブラケット。
  let hi = a + 10 * Math.sqrt(a) + 10;
  let guard = 0;
  while (regLowerGamma(a, hi) < q && guard < 100) {
    hi *= 2;
    guard++;
  }
  let lo = 0;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (regLowerGamma(a, mid) < q) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Gamma(shape=alpha, rate=beta) の確率 q 分位点。CDF(v)=P(alpha, beta·v)。 */
export function gammaQuantile(alpha: number, beta: number, q: number): number {
  return invRegLowerGamma(alpha, q) / beta;
}

/** 事後 Gamma の 90% 信用区間 [5%, 95%]（§5.2）。 */
export function gammaCI90(alpha: number, beta: number): [number, number] {
  return [gammaQuantile(alpha, beta, 0.05), gammaQuantile(alpha, beta, 0.95)];
}
